import { useEffect, useState } from 'react';
import calculateImpactMetrics from '@/organisms/plans/PlanImpact/utils/calculateImpactMetrics';
import mapAggregateImpact from '@/organisms/plans/utils/impact/mapAggregateImpact';
import { useScenarioContext, useViewModeContext } from '@/shared/providers';
import { useTagContext } from '@/shared/providers';
import ActionType from '@enums/ActionType';
import EntityType from '@enums/EntityType';
import aggregateMetrics from '../utils/aggregateMetrics';
import summaryMetricList from '../utils/summaryMetricList';
import summaryTagMetricList from '../utils/summaryTagMetricList';

const entityMetricsAsTotals = (selected) => {
  switch (selected.actionType) {
    case ActionType.UPDATED_ACTIVITY_TAGS:
      return !selected[EntityType.ACTIVITY];
    case ActionType.UPDATED_ROLE_TAGS:
      return !selected[EntityType.ROLE];
    case ActionType.UPDATED_GROUP_TAGS:
      return !selected[EntityType.GROUP];
    default:
      return false;
  }
};

const includeActivityCostMetrics = (selected) => {
  const type = selected.entity || selected.entityType;

  if (type !== EntityType.ACTIVITY && type !== EntityType.ROLE) {
    return false;
  }

  switch (selected.actionType) {
    case ActionType.UPDATED_ACTIVITY_TAGS:
    case ActionType.UPDATED_GROUP_TAGS:
    case ActionType.UPDATED_ROLE_TAGS:
      return false;
    default:
      return true;
  }
};

const useSummaryMetrics = () => {
  const { comparisonScenario, scenario } = useScenarioContext();
  const { showBudget } = useViewModeContext();
  const { tagMap } = useTagContext();

  const [selected, setSelected] = useState();
  const [comparisonMetrics, setComparisonMetrics] = useState();
  const [comparisonTotalMetrics, setComparisonTotalMetrics] = useState();
  const [impactMetrics, setImpactMetrics] = useState();
  const [metricList, setMetricList] = useState();
  const [tagMetricList, setTagMetricList] = useState();
  const [scenarioMetrics, setScenarioMetrics] = useState();
  const [scenarioTotalMetrics, setScenarioTotalMetrics] = useState();

  const selectSummaryItem = (summaryItem) => {
    if (!summaryItem || summaryItem.id === selected?.id) {
      setSelected(null);
      return;
    }
    setSelected(summaryItem);
  };

  useEffect(() => {
    if (!comparisonScenario || !scenario || !tagMap?.size) {
      return;
    }

    const comparisonMetrics = comparisonScenario
      ? aggregateMetrics(comparisonScenario)
      : null;
    const scenarioMetrics = scenario ? aggregateMetrics(scenario) : null;

    if (!selected) {
      const impactMap = mapAggregateImpact({
        comparisonScenario,
        scenario,
        showBudget,
      });

      const impactMetrics = calculateImpactMetrics({
        impactMap,
        scenarioMetrics,
        comparisonMetrics,
      });

      const metricList = summaryMetricList({
        comparisonMetrics,
        scenarioMetrics,
        showBudget,
        showActivityBudget: showBudget,
        showCost: showBudget,
      });
      const tagMetricList = summaryTagMetricList({
        comparisonMetrics,
        scenarioMetrics,
        showBudget,
        tagMap,
      });

      setComparisonMetrics(comparisonMetrics);
      setComparisonTotalMetrics(comparisonMetrics);
      setScenarioMetrics(scenarioMetrics);
      setScenarioTotalMetrics(scenarioMetrics);
      setImpactMetrics(impactMetrics);
      setMetricList(metricList);
      setTagMetricList(tagMetricList);

      return;
    }

    const showSpan =
      selected.entityType !== EntityType.MANAGER ||
      (selected.entityType === EntityType.MANAGER && selected.actions);

    const showEntityBudget =
      selected.entity !== EntityType.ACTIVITY &&
      selected.entityType !== EntityType.ACTIVITY
        ? showBudget
        : false;

    const showActivityCost = includeActivityCostMetrics(selected) && showBudget;

    const showEntityCost = showBudget;

    const showAllocatedFTE =
      selected.entity === EntityType.ACTIVITY ||
      selected.entityType === EntityType.ACTIVITY;

    const { metrics } = selected;

    const selectedComparisonMetrics = metrics[0];
    const selectedScenarioMetrics = metrics[1];

    const shouldUseEntityTotals = entityMetricsAsTotals(selected);

    const totalComparisonMetrics = shouldUseEntityTotals
      ? selectedComparisonMetrics
      : comparisonMetrics;

    const totalScenarioMetrics = shouldUseEntityTotals
      ? selectedScenarioMetrics
      : scenarioMetrics;

    const metricList = summaryMetricList({
      comparisonMetrics: selectedComparisonMetrics,
      scenarioMetrics: selectedScenarioMetrics,
      showAllocatedFTE,
      showBudget: showEntityBudget,
      showCost: showActivityCost,
      showActivityBudget: showActivityCost,
      showSpan,
    });
    const tagMetricList = summaryTagMetricList({
      comparisonMetrics: selectedComparisonMetrics,
      scenarioMetrics: selectedScenarioMetrics,
      showBudget,
      showCost: showBudget,
      tagMap,
    });

    setComparisonMetrics(selectedComparisonMetrics);
    setComparisonTotalMetrics(totalComparisonMetrics);
    setScenarioMetrics(selectedScenarioMetrics);
    setScenarioTotalMetrics(totalScenarioMetrics);
    setMetricList(metricList);
    setTagMetricList(tagMetricList);
  }, [
    JSON.stringify(scenario?.entity?.__metrics?.visible),
    JSON.stringify(comparisonScenario?.entity?.__metrics?.visible),
    selected,
    showBudget,
    scenario?.details?.hash,
    comparisonScenario?.details?.hash,
    tagMap?.size,
  ]);

  return {
    comparisonMetrics,
    comparisonTotalMetrics,
    comparisonTotalMetrics,
    impactMetrics,
    metricList,
    tagMetricList,
    scenarioMetrics,
    scenarioTotalMetrics,
    scenarioTotalMetrics,
    selectedSummaryItem: selected,
    selectSummaryItem,
  };
};

export default useSummaryMetrics;
